// src/pages/History.js
import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Hooks
import { useHistoryData } from "./hooks/useHistoryData";

// Components
import Login from "../login";
import GameHistory from "./components/GameHistory";
import PerformanceOverview from "./components/PerformanceOverview";
import ProUpgradeOverlay from "./components/ProUpgradeOverlay";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * History Component - Refactored for better maintainability and code quality
 *
 * Features:
 * - Separated data logic into custom hook
 * - Broken down into smaller, reusable components
 * - Improved performance with memoization
 * - Better error handling and loading states
 * - Cleaner separation of concerns
 */
const History = () => {
  const { t } = useTranslation();
  const { isLoggedIn, proMember } = useSelector((state) => state.user);

  const [timePeriod, setTimePeriod] = useState("all");
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [hoveredSessionId, setHoveredSessionId] = useState(null);

  // Use custom hook for data management
  const { darts, sessions, loading, error, statistics, findDartById } =
    useHistoryData(timePeriod);

  // Memoized callback functions for better performance
  const handleTimePeriodChange = useCallback((period) => {
    setTimePeriod(period);
  }, []);

  const toggleSessionDetail = useCallback((id) => {
    setExpandedSessionId((prev) => (prev === id ? null : id));
  }, []);

  const handleSetHoveredSessionId = useCallback((id) => {
    setHoveredSessionId(id);
  }, []);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-xl font-semibold mb-2">{t("common.error")}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-test mx-auto mb-4"></div>
          <p>{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Not logged in state
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen">
        <div className="max-w-5xl mx-auto space-y-6">
          <Login />
        </div>
      </div>
    );
  }

  // Pro member content
  const renderProContent = () => (
    <>
      <PerformanceOverview
        statistics={statistics}
        sessions={sessions}
        findDartById={findDartById}
        onTimePeriodChange={handleTimePeriodChange}
      />

      <GameHistory
        sessions={sessions}
        expandedSessionId={expandedSessionId}
        hoveredSessionId={hoveredSessionId}
        setHoveredSessionId={handleSetHoveredSessionId}
        toggleSessionDetail={toggleSessionDetail}
        findDartById={findDartById}
      />
    </>
  );

  // Non-pro member content with upgrade overlays
  const renderFreeContent = () => (
    <>
      <ProUpgradeOverlay
        title={t("history.proFeature")}
        description={t("history.upgradeToPro")}
        blurredContent={
          <div>
            <h2 className="text-xl font-semibold mb-4 text-test">
              {t("history.performanceOverview")}
            </h2>
            <div className="bg-primary/80 rounded-xl p-4 border border-primary/30 h-64 flex items-center justify-center">
              <p className="text-white text-center">
                {t("history.proFeature")}
              </p>
            </div>
          </div>
        }
      />

      <ProUpgradeOverlay
        title={t("history.proFeature")}
        description={t("history.upgradeToProGameHistory")}
        blurredContent={
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-test mb-4">
              {t("history.gameSessions")}
            </h2>
            <div className="bg-primary/80 rounded-xl p-4 border border-primary/30 h-64 flex items-center justify-center">
              <p className="text-white text-center">
                {t("history.proFeature")}
              </p>
            </div>
          </div>
        }
      />
    </>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        {proMember ? renderProContent() : renderFreeContent()}
      </div>
    </div>
  );
};

export default History;
